import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type AriaAttributes, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { matchOptions } from './lookupMatch';
import { measureReveal, planReveal, scrollParentOf } from './lookupPlacement';

/**
 * A typed lookup in place of a dropdown (Michael, 2026-10-08: "All of the
 * drop downs in the daily log need to be converted to typed fields with
 * lookup. much faster data entry"). The same WAI-ARIA combobox shape as the
 * invoice ProjectLookup and the Field Board JobPicker, made generic: any list
 * of `{ value, label }` choices, storing exactly the value the old <select>
 * stored.
 *
 *  - Type a few letters or digits: every word must hit the label (or the
 *    option's keywords), case and accents aside, best matches first
 *    (lookupMatch.ts). The best match is highlighted, so "150" Enter picks.
 *  - ArrowUp / ArrowDown move, Home / End jump, Enter picks, Tab takes the
 *    highlighted match and moves on, Escape puts the previous value back.
 *    Space with nothing typed opens the list on the current choice, as on a
 *    select (mid-search it types a space). Focus alone never opens the list —
 *    tabbing through a row of boxes does not pop lists over the next fields.
 *  - A tap on the box opens the whole list (the choices under their group
 *    headings); a tap on an option picks it. 44px rows on a phone. A click
 *    on a box that already has the focus, with nothing typed, keeps its text
 *    selected, so typing starts a new search; mid-search it places the caret.
 *  - The open list is fitted to what can be seen: the page scrolls the box up
 *    until its list ends above the keyboard (a phone) and above the log's
 *    footer stuck over the bottom (a desk; on a phone a list in the page's
 *    flow — a list over the page is drawn over the footer there), never
 *    under a bar pinned over the top; where the page cannot scroll that far,
 *    the list is shortened to the room there is, or opens above the box
 *    (lookupPlacement.ts).
 *  - `emptyLabel` offers the explicit empty choice the select had ("—"),
 *    listed first, and the × that clears; without it nothing empties. A box
 *    narrower than 120px (a table cell on a tablet) draws no × — it covered
 *    the middle, where a tap lands — and keeps that room for its text.
 *  - `keepUnknown`: a stored value no option carries shows as stored (or by
 *    `unknownLabel`), as the selects' extra <option> did; without it the box
 *    reads empty for such a value, as the select did. Nothing is rewritten
 *    until somebody picks.
 *  - Long lists draw the first `limit` (50) matches and say "type more to
 *    narrow".
 *
 * Where the list draws: under the box, over what follows (default); `inline`
 * in the flow under the box (inside a dimmed card, where an overlay would be
 * see-through); or into `listHost` — for a row that scrolls sideways, which
 * would clip a list drawn inside it.
 */

export interface LookupOption {
  /** What is stored. */
  value: string;
  /** What is shown and searched. */
  label: string;
  /** Heading the option sits under while the box is empty (Pinned ★ / Recent / All). */
  group?: string;
  /** More words that find the option without being shown (a role's short names: "pm", "super"). */
  keywords?: readonly string[];
  /** A short note at the right of the option. */
  hint?: string;
  /**
   * Listed only once something is typed — a long list behind a short one
   * (the company directory under the job's crew, 2026-10-17: "Not on this
   * job"), so a tap on the box never draws the whole company.
   */
  searchOnly?: boolean;
}

type DataAttributes = { [key: `data-${string}`]: string | undefined };

export interface LookupFieldProps extends DataAttributes {
  value: string;
  onChange: (value: string) => void;
  options: readonly LookupOption[];
  /** The input's id — for a <label htmlFor> outside. */
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: AriaAttributes['aria-invalid'];
  'aria-required'?: AriaAttributes['aria-required'];
  /** What the list and the clear button are called after; defaults to aria-label. */
  label?: string;
  /** The explicit empty choice, listed first; also turns on the × clear. Omit when empty is not an answer. */
  emptyLabel?: string;
  /** Show a stored value no option carries (as typed, or by unknownLabel). */
  keepUnknown?: boolean;
  unknownLabel?: (value: string) => string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Classes for the field's outer box (its width, its place in a grid). */
  className?: string;
  /** Classes for the input itself (the page's own field look). */
  inputClassName?: string;
  /** Most options drawn at once; typing reaches the rest. */
  limit?: number;
  /** Said under the list while nothing is typed and searchOnly options wait behind it. */
  searchHint?: string;
  /**
   * Take the focus and open the list as the box mounts — an editor a tap or
   * Enter opened in a table cell (Time Entry's Crew Day, 2026-10-17), so a tap
   * still shows the choices at once, as the cell's select did.
   */
  openOnMount?: boolean;
  /** Draw the list in the flow under the box instead of over what follows. */
  inline?: boolean;
  /**
   * Draw the open list into this element instead (in the flow); closed, it
   * stays hidden in place. A ref, read when the list opens — by then the
   * element is on the page, and no state is set while the page mounts.
   */
  listHost?: RefObject<HTMLElement>;
}

type EntryKind = 'empty' | 'unknown' | 'option';
interface Entry extends LookupOption { kind: EntryKind }

const DEFAULT_LIMIT = 50;
/** activeKey marker: the first entry, whatever the list now holds (the best match while typing). */
const FIRST = '\u0000first';
const EMPTY_KEY = '\u0000empty';
const UNKNOWN_KEY = '\u0000unknown';
const keyOf = (e: Entry) => (e.kind === 'empty' ? EMPTY_KEY : e.kind === 'unknown' ? UNKNOWN_KEY : `o:${e.value}`);
/** Stored ids may come back in another case (SQL Server upper-cases uuids). */
const sameValue = (a: string, b: string) => a === b || a.toLowerCase() === b.toLowerCase();

/**
 * Narrower than this, a box draws no × and only a slim chevron (review 2 of
 * 2026-10-08): in a 67-74px table cell the 40px × covered the middle, so a
 * tap there emptied Hauler or Source and the next save sent the blank, and
 * 15-22px was left for the text. "—" in the list still empties the box.
 */
const NARROW_BOX = 120;

const DEFAULT_INPUT =
  'h-11 w-full rounded-md border border-graphite-600 bg-graphite-800 px-3 text-base text-graphite-100 placeholder:text-graphite-500 focus:border-safety-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm';

/** Options in group order (first appearance), each group's members in their own order. */
function groupOrder(entries: Entry[]): Entry[] {
  if (!entries.some((e) => e.group)) return entries;
  const groups = new Map<string, Entry[]>();
  for (const e of entries) {
    const g = e.group ?? '';
    const members = groups.get(g);
    if (members) members.push(e);
    else groups.set(g, [e]);
  }
  return [...groups.values()].flat();
}

function isCoarsePointer(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** How an open list was fitted to what can be seen (null: under the box, at its usual height). */
interface Fit { above: boolean; maxHeight: number | null }
const sameFit = (a: Fit | null, b: Fit | null) => a === b || (!!a && !!b && a.above === b.above && a.maxHeight === b.maxHeight);

export function LookupField({
  value, onChange, options, id, label, emptyLabel, keepUnknown = false, unknownLabel, placeholder,
  disabled = false, readOnly = false, className, inputClassName, limit = DEFAULT_LIMIT, inline = false, listHost, searchHint, openOnMount = false,
  'aria-label': ariaLabel, 'aria-describedby': describedBy, 'aria-invalid': invalid, 'aria-required': required,
  ...rest
}: LookupFieldProps) {
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (i: number) => `${uid}-opt-${i}`;
  const name = label ?? ariaLabel ?? 'Choice';
  const interactive = !disabled && !readOnly;
  const dataAttrs = Object.fromEntries(Object.entries(rest).filter(([k]) => k.startsWith('data-')));

  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  /** The typed text; null while not typing (the box shows the value's label). */
  const [query, setQuery] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  /** Bumped when a pick or Escape closes the list: the box's text is then selected, so typing replaces it. */
  const [settled, setSettled] = useState(0);
  /** Set once the list is open where it would not be seen whole: shortened to the room, or above the box. */
  const [fit, setFit] = useState<Fit | null>(null);
  const fitted = open ? fit : null;
  /** Narrower than NARROW_BOX (a table cell on a tablet): no ×, a slim chevron. */
  const [narrow, setNarrow] = useState(false);

  const current = useMemo(() => {
    if (value === '') return null;
    return options.find((o) => o.value === value) ?? options.find((o) => sameValue(o.value, value)) ?? null;
  }, [options, value]);
  const unknownEntry = useMemo<Entry | null>(
    () => (value !== '' && !current && keepUnknown ? { kind: 'unknown', value, label: unknownLabel?.(value) ?? value } : null),
    [value, current, keepUnknown, unknownLabel],
  );
  const display = current?.label ?? unknownEntry?.label ?? '';
  const currentKey = value === '' ? (emptyLabel !== undefined ? EMPTY_KEY : null) : current ? `o:${current.value}` : unknownEntry ? UNKNOWN_KEY : null;

  const typed = query !== null && query.trim() !== '';
  // Worked out only while the list is open: a page of closed boxes (a crew of
  // thirty, two each) costs nothing per keystroke elsewhere on the page. The
  // entries hold still while one box is typed in, so their folded text is
  // worked out once (lookupMatch caches it per entry).
  const optionEntries = useMemo<Entry[]>(() => (open ? options.map((o) => ({ ...o, kind: 'option' })) : []), [open, options]);
  const { shown, total } = useMemo(() => {
    if (!open) return { shown: [] as Entry[], total: 0 };
    const fixed: Entry[] = [];
    let matched: Entry[];
    if (!typed) {
      if (emptyLabel !== undefined) fixed.push({ kind: 'empty', value: '', label: emptyLabel });
      if (unknownEntry) fixed.push(unknownEntry);
      matched = groupOrder(optionEntries.filter((e) => !e.searchOnly));
    } else {
      matched = matchOptions(unknownEntry ? [unknownEntry, ...optionEntries] : optionEntries, query ?? '');
    }
    return { shown: [...fixed, ...matched.slice(0, limit)], total: matched.length };
  }, [open, typed, query, emptyLabel, unknownEntry, optionEntries, limit]);

  const activeIndex = !open || activeKey === null ? -1
    : activeKey === FIRST ? (shown.length > 0 ? 0 : -1)
    : shown.findIndex((e) => keyOf(e) === activeKey);

  // Keep the highlighted option in sight — by scrolling the list only, never
  // the page under it (scrollIntoView would move every scrolling ancestor).
  useEffect(() => {
    const list = listRef.current;
    const option = activeIndex >= 0 ? document.getElementById(optionId(activeIndex)) : null;
    if (!list || !option) return;
    const l = list.getBoundingClientRect();
    const o = option.getBoundingClientRect();
    if (o.top < l.top) list.scrollTop -= l.top - o.top;
    else if (o.bottom > l.bottom) list.scrollTop += o.bottom - l.bottom;
    // optionId is a pure function of uid; a list just shortened (fitted) is looked at again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, open, fitted]);

  useEffect(() => {
    if (!openOnMount || !interactive) return;
    inputRef.current?.focus();
    openList();
    // Once, as the box mounts: a later prop change never reopens it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A box that turns disabled or read-only (the log was submitted) shuts its list.
  useEffect(() => {
    if (!interactive) {
      setOpen(false);
      setQuery(null);
      setFit(null);
    }
  }, [interactive]);

  // How wide the box is drawn — measured before the first paint, and again
  // whenever it changes (a window resized, a section opened). Zero: not laid
  // out (hidden, or a test page) — nothing is known, so nothing changes.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      setNarrow(width > 0 && width < NARROW_BOX);
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // After a pick or Escape the text is selected, so the next keystroke starts a new search.
  useLayoutEffect(() => {
    if (settled > 0 && document.activeElement === inputRef.current) inputRef.current?.select();
  }, [settled]);

  // Once the list is open, fit it to what can be seen: scroll just enough for
  // it to end above what covers the bottom — a phone's keyboard, the log's
  // footer stuck over the bottom (on a phone a list drawn over the page is
  // drawn over the footer too) — never the box off the top, nor under a bar
  // stuck there; and where the page cannot scroll that far, fit the list to
  // the room left: shorter, or above the box (lookupPlacement.ts).
  // A desk does it at once: no keyboard to wait for, and the list should
  // simply be there, as a select's was (review 2 of 2026-10-08: a desk never
  // did it, and lists opened under the footer). A phone waits a moment for
  // its keyboard, then looks again whenever the visual viewport shrinks as
  // the keyboard arrives. Only on a shrink: iOS resizes the viewport as its
  // toolbar comes and goes while someone scrolls, and that must not pull the
  // page back under their finger.
  useEffect(() => {
    if (!open) return;
    const touch = isCoarsePointer();
    const reveal = () => {
      const input = inputRef.current;
      const list = listRef.current;
      if (!input || !list) return;
      const plan = planReveal(measureReveal(input, list, inline || !!listHost?.current, touch));
      const next = plan.above || plan.maxHeight !== null ? { above: plan.above, maxHeight: plan.maxHeight } : null;
      setFit((prev) => (sameFit(prev, next) ? prev : next));
      if (plan.scroll !== 0) {
        const target = scrollParentOf(input) ?? document.scrollingElement;
        // A phone glides while its keyboard rises; a desk jumps, so the list is simply there (as a select's was).
        target?.scrollBy?.({ top: plan.scroll, behavior: touch ? 'smooth' : 'instant' });
      }
    };
    if (!touch) {
      reveal();
      return;
    }
    let lastHeight = window.visualViewport?.height ?? window.innerHeight;
    let settle: number | undefined;
    const timer = window.setTimeout(reveal, 150);
    const vv = window.visualViewport;
    const onResize = () => {
      const height = vv?.height ?? window.innerHeight;
      const shrank = height < lastHeight;
      lastHeight = height;
      if (!shrank) return;
      window.clearTimeout(settle);
      settle = window.setTimeout(reveal, 150);
    };
    vv?.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(settle);
      vv?.removeEventListener('resize', onResize);
    };
  }, [open, inline, listHost]);

  function openList() {
    if (!interactive) return;
    setOpen(true);
    setQuery(null);
    setActiveKey(currentKey);
  }

  function close() {
    setOpen(false);
    setQuery(null);
    setActiveKey(null);
    setFit(null); // each opening starts under the box, at its usual height
  }

  function choose(entry: Entry) {
    if (!sameValue(entry.value, value)) onChange(entry.value);
    close();
    setSettled((n) => n + 1);
  }

  function move(to: number) {
    const e = shown[Math.max(0, Math.min(to, shown.length - 1))];
    if (e) setActiveKey(keyOf(e));
  }

  function onType(text: string) {
    if (!interactive) return;
    setQuery(text);
    setOpen(true);
    // Typing: the best match. Erased to nothing: the empty choice (Enter /
    // Tab then clears), else the current one. Only spaces: the current one,
    // so Enter or Tab keeps it — a phone keyboard's space bar reaches here
    // with no keydown to catch ("Unidentified" on Android).
    setActiveKey(text.trim() !== '' ? FIRST : text === '' && emptyLabel !== undefined ? EMPTY_KEY : currentKey);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!interactive || e.nativeEvent.isComposing) return;
    switch (e.key) {
      case ' ':
        // As on a select: with nothing typed yet, Space opens the list on the
        // current choice instead of replacing the box's selected text with a
        // space. Mid-search it types one ("general labor").
        if (query === null) {
          e.preventDefault();
          if (!open) openList();
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else move(activeIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList();
        else move(activeIndex - 1);
        break;
      case 'Home':
      case 'End':
        if (open && shown.length > 0) {
          e.preventDefault();
          move(e.key === 'Home' ? 0 : shown.length - 1);
        }
        break;
      case 'Enter':
        // Never submits a surrounding form mid-search.
        e.preventDefault();
        if (!open) openList();
        else if (activeIndex >= 0) choose(shown[activeIndex]);
        break;
      case 'Escape':
        if (open || query !== null) {
          // The open list takes this Escape; a sheet around it gets the next.
          e.preventDefault();
          e.stopPropagation();
          close();
          setSettled((n) => n + 1);
        }
        break;
      case 'Tab':
        // Takes the highlighted match and lets the focus move on.
        if (open) {
          if (activeIndex >= 0) choose(shown[activeIndex]);
          else close();
        }
        break;
      default:
        break;
    }
  }

  const isCurrent = (e: Entry) =>
    e.kind === 'empty' ? value === '' : e.kind === 'unknown' ? true : value !== '' && sameValue(e.value, value);

  function renderEntry(e: Entry, i: number) {
    const active = i === activeIndex;
    return (
      <li
        key={`${keyOf(e)}#${i}`}
        id={optionId(i)}
        role="option"
        aria-selected={active}
        data-value={e.value}
        onMouseDown={(ev) => ev.preventDefault()} // the box keeps the focus, so blur never shuts the list before the click
        onMouseMove={() => { if (!active) setActiveKey(keyOf(e)); }}
        onClick={() => choose(e)}
        className={cn(
          'flex min-h-touch cursor-pointer items-center gap-2 px-3 py-2 text-base sm:min-h-0 sm:text-sm',
          active ? 'bg-graphite-700 text-graphite-50' : 'text-graphite-200',
          e.kind !== 'option' && !active && 'text-graphite-300',
        )}
      >
        <span className="min-w-0 flex-1 break-words">{e.label}</span>
        {e.hint && <span className="shrink-0 text-xs text-graphite-400">{e.hint}</span>}
        {isCurrent(e) && <Check className="h-4 w-4 shrink-0 text-safety-500" aria-hidden="true" />}
      </li>
    );
  }

  function renderEntries(): ReactNode {
    if (!typed && shown.some((e) => e.group)) {
      // Runs of one group under its heading; the empty / kept choices first, on their own.
      const runs: { group: string | null; items: { e: Entry; i: number }[] }[] = [];
      shown.forEach((e, i) => {
        const g = e.kind === 'option' ? e.group ?? null : null;
        const last = runs[runs.length - 1];
        if (last && last.group === g) last.items.push({ e, i });
        else runs.push({ group: g, items: [{ e, i }] });
      });
      return runs.map((run, r) => (run.group === null
        ? run.items.map(({ e, i }) => renderEntry(e, i))
        : (
          <li key={`g:${run.group}:${r}`} role="group" aria-label={run.group}>
            <div aria-hidden="true" className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-graphite-400">
              {run.group}
            </div>
            <ul role="presentation">{run.items.map(({ e, i }) => renderEntry(e, i))}</ul>
          </li>
        )));
    }
    return shown.map((e, i) => renderEntry(e, i));
  }

  const listbox = (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label={`${name} choices`}
      hidden={!open}
      onMouseDown={(ev) => ev.preventDefault()} // dragging the list's scrollbar must not blur the box
      // Inside a <label> (the forms wrap a field in one), a click in the list
      // would be passed on to the box as a second click — reopening the list
      // just shut by a pick. A cancelled click is not passed on.
      onClick={(ev) => ev.preventDefault()}
      className={cn(
        'max-h-60 overflow-y-auto overscroll-contain rounded-md border border-graphite-700 bg-graphite-900 py-1 shadow-xl',
        listHost?.current || inline ? 'mt-1 w-full'
          : fitted?.above ? 'absolute inset-x-0 bottom-full z-30 mb-1'
          : 'absolute inset-x-0 top-full z-30 mt-1',
      )}
      style={fitted?.maxHeight != null ? { maxHeight: fitted.maxHeight } : undefined}
    >
      {open && (
        <>
          {renderEntries()}
          {!typed && searchHint && options.some((o) => o.searchOnly) && (
            <li role="presentation" className="px-3 py-2 text-xs text-graphite-400">{searchHint}</li>
          )}
          {typed && total === 0 && (
            <li role="presentation" className="px-3 py-2 text-sm text-graphite-400">No matches for “{query?.trim()}”.</li>
          )}
          {total > limit && (
            <li role="presentation" className="px-3 py-2 text-xs text-graphite-400">
              First {limit} of {total} — type more to narrow.
            </li>
          )}
        </>
      )}
    </ul>
  );

  const canClear = interactive && emptyLabel !== undefined && value !== '' && !narrow;

  return (
    <div ref={boxRef} className={cn('relative', className)} data-lookup="">
      {/* The box and its × / chevron; an inline list sits below this, never under the icon. */}
      <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        aria-required={required}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={interactive ? placeholder : (emptyLabel ?? placeholder)}
        value={query ?? display}
        data-value={value}
        {...dataAttrs}
        onChange={(e) => onType(e.target.value)}
        onKeyDown={onKeyDown}
        // Focus selects the text, so typing replaces it; a click opens the
        // list and selects it again. A click on a box that already has the
        // focus would leave a caret in the text instead — Chrome collapses
        // the selection on the mouse's release, after the click handler — and
        // typing then went into the middle of the label and found nothing
        // ("Partly Cloudysn", review 2 of 2026-10-08). So while nothing is
        // typed the release is cancelled; mid-search a click places the caret.
        onFocus={(e) => e.currentTarget.select()}
        onMouseUp={(e) => { if (interactive && query === null) e.preventDefault(); }}
        onClick={(e) => {
          if (!interactive) return;
          if (!open) openList();
          if (query === null) e.currentTarget.select();
        }}
        onBlur={() => { if (open || query !== null) close(); }}
        className={cn(DEFAULT_INPUT, inputClassName, interactive && (narrow ? 'pr-5' : 'pr-10'))}
      />
      {canClear ? (
        // A span, not a <button>: inside a wrapping <label> a button is a
        // second labelled control, and the label's clicks can land on it —
        // emptying the box. Not a tab stop (keyboard: erase the text and
        // Enter); the cancelled click is not passed on to the box.
        <span
          role="button"
          tabIndex={-1}
          aria-label={`Clear ${name}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => { e.preventDefault(); onChange(''); close(); inputRef.current?.focus(); }}
          className="absolute inset-y-0 right-0 flex min-h-touch w-10 cursor-pointer items-center justify-center rounded-r-md text-graphite-400 hover:text-graphite-100 sm:min-h-0"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : interactive && (
        <span
          aria-hidden="true"
          className={cn('pointer-events-none absolute inset-y-0 right-0 flex items-center justify-center text-graphite-400', narrow ? 'w-5' : 'w-10')}
        >
          <ChevronDown className={narrow ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </span>
      )}
      </div>
      {/* Into the host only while open: a closed list stays here, hidden, so an
          empty host takes no room (and aria-controls always finds the list). */}
      {open && listHost?.current ? createPortal(listbox, listHost.current) : listbox}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {open && typed ? (total === 0 ? 'No matches' : `${total} ${total === 1 ? 'match' : 'matches'}`) : ''}
      </span>
    </div>
  );
}
